import { Component, effect, inject, signal } from '@angular/core';
import {
  CapitalizePipe,
  ClientOrder,
  ClientOrderItem,
  ClientOrderService,
  errorToast,
  FormErrorService,
  successSaveToast,
  SupplierInvoiceItem,
  SupplierInvoiceItemMapping,
  SupplierInvoiceItemMappingService,
  SupplierInvoiceSection,
  AMOUNT_FORMAT,
  roundAmount,
} from 'shared';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { take } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { NotificationService } from '@progress/kendo-angular-notification';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DialogActionsComponent, DialogContentBase, DialogRef } from '@progress/kendo-angular-dialog';
import { ButtonComponent } from '@progress/kendo-angular-buttons';
import { UpperCasePipe } from '@angular/common';
import { ClientOrderItemTableComponent } from '@features/purchase/supplier-invoices/supplier-invoice-order-item-link-popup/client-order-item-table/client-order-item-table.component';
import { LoaderComponent } from '@progress/kendo-angular-indicators';
import { NumberFormatOptions, NumberPipe } from '@progress/kendo-angular-intl';

@Component({
  selector: 'rc-supplier-invoice-order-item-link-popup',
  imports: [
    ButtonComponent,
    CapitalizePipe,
    DialogActionsComponent,
    ReactiveFormsModule,
    TranslateModule,
    UpperCasePipe,
    ClientOrderItemTableComponent,
    LoaderComponent,
    NumberPipe,
  ],
  providers: [FormErrorService, TranslateService, CapitalizePipe],
  templateUrl: './supplier-invoice-order-item-link-popup.component.html',
  styleUrl: './supplier-invoice-order-item-link-popup.component.scss',
})
export class SupplierInvoiceOrderItemLinkPopupComponent extends DialogContentBase {
  clientOrder = signal<undefined | string>(undefined);
  co = signal<ClientOrder | undefined>(undefined);
  supplierInvoiceItem = signal<undefined | SupplierInvoiceItem>(undefined);
  selectedOrderItem = signal<undefined | ClientOrderItem>(undefined);
  section = signal<undefined | SupplierInvoiceSection>(undefined);
  supplierInvoiceItemLinkForm: FormGroup<SupplierInvoicerItemLinkForm> = this.#initForm();

  #supplierInvoiceItemMappingsService = inject(SupplierInvoiceItemMappingService);
  #notificationService = inject(NotificationService);
  #capitalizePipe = inject(CapitalizePipe);
  #translateService = inject(TranslateService);
  #formErrorService = inject(FormErrorService);
  #clientOrderService = inject(ClientOrderService);
  decimalFormat: NumberFormatOptions = {
    style: 'decimal',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  };
  protected readonly amountFormat = AMOUNT_FORMAT;

  constructor() {
    const dialog = inject(DialogRef);

    super(dialog);

    effect(() => {
      if (this.clientOrder() && !this.co()) {
        this.#clientOrderService.getClientOrderByIri(this.clientOrder()!).subscribe({
          next: (clientOrder) => {
            this.co.set(clientOrder);
          },
          error: (err: HttpErrorResponse) => {
            const notification = this.#notificationService.show(errorToast);
            if (notification.content) {
              notification.content.instance.setCurrentValue(err);
            }
            this.#formErrorService.setErrorViolations(err);
          },
        });
      }
    });

    effect(() => {
      const sii = this.supplierInvoiceItem();
      if (sii) {
        this.supplierInvoiceItemLinkForm.patchValue({
          supplierInvoiceItem: sii['@id']!,
        });
      }
    });
    effect(() => {
      const soi = this.selectedOrderItem();
      if (soi) {
        this.supplierInvoiceItemLinkForm.patchValue({
          clientOrderItem: soi['@id']!,
        });
      }
    });
  }

  onSubmit() {
    this.#supplierInvoiceItemMappingsService
      .post(this.supplierInvoiceItemLinkForm.value as SupplierInvoiceItemMapping)
      .pipe(take(1), distinctUntilChanged())
      .subscribe({
        next: (response) => {
          const notification = this.#notificationService.show(successSaveToast);
          if (notification.content) {
            notification.content.instance.value = this.#capitalizePipe.transform(
              this.#translateService.instant('SUPPLIER_INVOICE_ITEM') +
                ': ' +
                this.supplierInvoiceItem()!.articleNumber,
            );
          }
          // this.selectedOrderItem.set(response);
          this.dialog.close({ isSuccessFull: true, result: response });
        },
        error: (err: HttpErrorResponse) => {
          const notification = this.#notificationService.show(errorToast);
          if (notification.content) {
            notification.content.instance.setCurrentValue(err);
          }
          this.#formErrorService.setErrorViolations(err);
        },
      });
  }

  patchSupplierInvoiceItemForm(clientOrderItem: ClientOrderItem | undefined) {
    this.selectedOrderItem.set(clientOrderItem);
    this.supplierInvoiceItemLinkForm.patchValue({
      clientOrderItem: clientOrderItem ? clientOrderItem['@id'] : null,
      // at most what is still open on the client order line (part of it may be linked to other invoice lines)
      amount: Math.min(
        clientOrderItem ? Math.max(roundAmount(clientOrderItem.amount - (Number(clientOrderItem.mappedAmount) || 0)), 0) : 0,
        this.supplierInvoiceItem()!.amountToMap,
      ),
    });
  }

  #initForm() {
    const form = new FormGroup<SupplierInvoicerItemLinkForm>({
      clientOrderItem: new FormControl(null, {
        nonNullable: false,
        validators: [Validators.required],
      }),
      amount: new FormControl(1, {
        nonNullable: true,
        validators: [Validators.min(1)],
      }),
      supplierInvoiceItem: new FormControl('', {
        nonNullable: true,
        validators: [Validators.min(1)],
      }),
    });
    form.updateValueAndValidity();
    return form;
  }

  close() {
    this.supplierInvoiceItemLinkForm.reset();
    this.dialog.close();
  }
}
interface SupplierInvoicerItemLinkForm {
  clientOrderItem: FormControl<string | null>;
  amount: FormControl<number>;
  supplierInvoiceItem: FormControl<string>;
}
